import {
  DEVICE_TOKEN_HEADER,
  adoptDraftsResultSchema,
  authProblemSchema,
  calcErrorSchema,
  validationProblemSchema,
  characterListOutputSchema,
  characterSheetSchema,
  createCharacterResultSchema,
  creationEditSchema,
  editCreationResultSchema,
  type CreationChoices,
  type CreationEdit,
  type EditCreationResult,
  creationPlanSchema,
  creationProblemSchema,
  exportCharacterResultSchema,
  importCharacterResultSchema,
  importProblemSchema,
  type ExportCharacterResult,
  type ImportCharacterResult,
  type ImportProblem,
  contentCopyOutputSchema,
  contentFormOutputSchema,
  contentPickOutputSchema,
  contentProblemSchema,
  customListOutputSchema,
  customRecordResultSchema,
  effectVocabularySchema,
  removeCustomResultSchema,
  type ContentCopy,
  type ContentPick,
  type ContentProblem,
  type CustomRecord,
  type CustomType,
  type EffectVocabulary,
  type FormField,
  type SaveCustomInput,
  classSpellsOutputSchema,
  hpCommandResultSchema,
  hpProblemSchema,
  inventoryCommandResultSchema,
  inventoryProblemSchema,
  levelPlanSchema,
  levelUpCommandResultSchema,
  rollCommandResultSchema,
  rollLogOutputSchema,
  spellCommandResultSchema,
  spellProblemSchema,
  resourceProblemSchema,
  type CharacterSheet,
  type CharacterSummary,
  type CreateCharacterInput,
  type CreationPlan,
  type CreationPlanInput,
  type CreationProblem,
  type HpAction,
  type HpProblem,
  type InventoryAction,
  type InventoryProblem,
  type LevelPlan,
  type LevelUpCommandInput,
  type RollCommandInput,
  type RollCommandResult,
  type RollEntry,
  type SheetSpell,
  type SpellAction,
  type ResourceAction,
  type ResourceProblem,
  type UpdateCharacterInput,
  type SpellProblem,
  confirmEmailResultSchema,
  loginResultSchema,
  logoutResultSchema,
  meResultSchema,
  refreshResultSchema,
  registerResultSchema,
  resendConfirmationResultSchema,
  requestPasswordResetResultSchema,
  resetPasswordResultSchema,
  type AdoptDraftsResult,
  type AuthProblem,
  type CalcError,
  type AuthTokens,
  type LoginInput,
  type LoginResult,
  type MeResult,
  type RegisterInput,
  type ResetPasswordInput,
  campaignRollsOutputSchema,
  declareRestResultSchema,
  initiativeResultSchema,
  masterCatalogueSchema,
  monsterListOutputSchema,
  masterCommandResultSchema,
  partyOutputSchema,
  type CampaignRoll,
  type DeclareRestResult,
  type InitiativeState,
  type MasterCatalogue,
  type MonsterListEntry,
  type MasterCommand,
  type PartyMember,
  campaignListOutputSchema,
  campaignResultSchema,
  seatListOutputSchema,
  seatResultSchema,
  releaseSeatResultSchema,
  inviteResultSchema,
  joinRequestListOutputSchema,
  resolveJoinRequestResultSchema,
  claimInviteResultSchema,
  mySeatsOutputSchema,
  invitePreviewSchema,
  requestJoinResultSchema,
  type Campaign,
  type ClaimInviteResult,
  type CreateCampaignInput,
  type InvitePreview,
  type InviteResult,
  type JoinCharacterOrigin,
  type JoinCharacterRef,
  type JoinRequest,
  type ReleaseSeatResult,
  type RequestJoinResult,
  type Seat,
  type SeatState,
  type UpdateCampaignInput,
  type ValidationProblem,
} from '@rollcall/contracts'
import { z } from 'zod'
import {
  openCampaignStream,
  type CampaignStream,
  type StreamHandlers,
} from './events.js'

/**
 * Клиент серверных процедур.
 *
 * Типы не выводятся из роутера сервера: `apps/web` не импортирует
 * `apps/api` (architecture.md, раздел 2). Общее у них — схемы
 * из `packages/contracts`, и ответ здесь ими же и разбирается
 * (CLAUDE.md, п. 6). Разбор не формальность: сервер может ответить
 * старой формой после выката, и лучше поймать это здесь, чем
 * половиной листа персонажа.
 *
 * Расчётов в этом файле нет и быть не должно: клиент показывает
 * то, что посчитал сервер (D3).
 */

/** Ответ tRPC поверх HTTP: значение под `result.data`. */
const successSchema = z.object({ result: z.object({ data: z.unknown() }) })

/**
 * Отказ tRPC. Замечания по слотам сервер кладёт в `data.problems` —
 * `cause` по проводу не передаётся (см. errorFormatter в `apps/api`).
 */
const failureSchema = z.object({
  error: z.object({
    message: z.string(),
    data: z
      .object({
        problems: z.array(creationProblemSchema).optional(),
        inventory: z.array(inventoryProblemSchema).optional(),
        hp: z.array(hpProblemSchema).optional(),
        spells: z.array(spellProblemSchema).optional(),
        /** Отказ счётчика ресурса: «ярость кончилась», «и так целиком». */
        resources: z.array(resourceProblemSchema).optional(),
        /** Отказ редактора контента: поле, ключ, «запись используют». */
        content: z.array(contentProblemSchema).optional(),
        /** Отказ импорта файла персонажа: чего не хватает и почему. */
        import: z.array(importProblemSchema).optional(),
        auth: authProblemSchema.optional(),
        /** Почему не посчитался лист: путь, цикл, спорящие `set`. */
        calculation: calcErrorSchema.optional(),
        /** Что не так со значением поля: адрес, длина пароля. */
        validation: z.array(validationProblemSchema).optional(),
      })
      .partial()
      .optional(),
  }),
})

/** Отказ создания: замечания привязаны к слотам, их показывает визард. */
export class CreationRefused extends Error {
  constructor(readonly problems: readonly CreationProblem[]) {
    super('создание отклонено сервером')
    this.name = 'CreationRefused'
  }
}

/**
 * Отказ в действии с инвентарём: замечания привязаны к предмету,
 * их показывает вкладка у его строки, а не общей полосой сверху.
 */
export class InventoryRefused extends Error {
  constructor(readonly problems: readonly InventoryProblem[]) {
    super('действие с инвентарём отклонено сервером')
    this.name = 'InventoryRefused'
  }
}

/** Отказ в изменении хитов: лечить полного и бить мёртвого нечем. */
export class HpRefused extends Error {
  constructor(readonly problems: readonly HpProblem[]) {
    super('изменение хитов отклонено сервером')
    this.name = 'HpRefused'
  }
}

/** Отказ в действии с заклинаниями: ячейки, подготовка, сосредоточение. */
export class SpellRefused extends Error {
  constructor(readonly problems: readonly SpellProblem[]) {
    super('действие с заклинаниями отклонено сервером')
    this.name = 'SpellRefused'
  }
}

/** Отказ в действии со счётчиком ресурса (D56). */
export class ResourceRefused extends Error {
  constructor(readonly problems: readonly ResourceProblem[]) {
    super('действие со счётчиком отклонено сервером')
    this.name = 'ResourceRefused'
  }
}

/**
 * Отказ редактора контента (D53).
 *
 * Отдельный вид отказа, потому что «поле не прошло схему» и «запись
 * используют три персонажа» — разные события: второе не ошибка,
 * а вопрос, на который редактор просит ответить числом.
 */
export class ContentRefused extends Error {
  constructor(readonly problems: readonly ContentProblem[]) {
    super('запись контента отклонена сервером')
    this.name = 'ContentRefused'
  }
}

/**
 * Файл персонажа не принят (D54).
 *
 * Список, а не строка: человеку нужно видеть, каких записей не хватает
 * и есть ли они в файле, — от этого зависит, предложить ли завести их
 * у себя.
 */
export class ImportRefused extends Error {
  constructor(readonly problems: readonly ImportProblem[]) {
    super('файл персонажа отклонён сервером')
    this.name = 'ImportRefused'
  }
}

/**
 * Лист не посчитался.
 *
 * Третий случай рядом с «нет такого» и «сервер недоступен», и путать
 * его с ними нельзя: сеть при этом работает, ссылка верная, а не
 * сходится расчёт — и человеку нужно сказать, что именно.
 *
 * Причина приходит кодом с полями (`CalcError`), а не текстом: текст
 * собирает интерфейс на языке профиля (D11).
 */
export class CalculationFailed extends Error {
  constructor(readonly detail: CalcError) {
    super('лист не посчитался')
    this.name = 'CalculationFailed'
  }
}

/**
 * Отказ аутентификации: код, а не текст (D8).
 *
 * По нему экран решает, показать ли «неверный адрес или пароль»,
 * «подтвердите почту» или выйти совсем. Разбирать текст сообщения
 * нельзя: он меняется от правки запятой, а экраны от неё зависеть
 * не должны.
 */
export class AuthRefused extends Error {
  constructor(readonly problem: AuthProblem) {
    super('аутентификация отклонена')
    this.name = 'AuthRefused'
  }
}

/**
 * Замечание к полю формы: адрес не похож на адрес, пароль короток.
 *
 * Отдельно от `AuthRefused`: тот про решение сервера, а это про
 * значение, которое до решения не доехало. Раньше оба случая
 * показывались одним «Не вышло. Попробуйте ещё раз».
 */
export class ValidationRefused extends Error {
  constructor(readonly problems: readonly ValidationProblem[]) {
    super('значения не прошли проверку')
    this.name = 'ValidationRefused'
  }
}

/** Всё остальное: сеть, пятисотка, неожиданная форма ответа. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export type Fetch = typeof globalThis.fetch

/**
 * Сессия мастера со стороны запроса (D8).
 *
 * Два метода вместо одного — это два из трёх слоёв гонки, из-за
 * которой в предыдущем проекте разлогинивало через пятнадцать минут.
 *
 * `accessTokenFor` решает, класть ли токен: на `auth.login`
 * и `auth.refresh` он не кладётся никогда. Токен на входе бессмыслен,
 * а токен на обновлении — это протухший токен, отправленный туда,
 * где его проверять не должны.
 *
 * `mayRefresh` решает, можно ли по отказу пойти обновляться. На тех же
 * процедурах — нельзя: отказ обновления, ведущий к обновлению, и есть
 * бесконечный цикл.
 *
 * Третий слой — мьютекс — живёт в самой реализации
 * (`apps/web/src/auth/session.ts`): десять параллельных запросов
 * обязаны дать одно обновление, а не десять.
 */
export type SessionPort = {
  /** Токен доступа для этой процедуры; `null` — ей он не положен. */
  accessTokenFor(path: string): string | null
  /** Можно ли обновиться, если эта процедура ответила отказом. */
  mayRefresh(path: string): boolean
  /** Обновить токены. `false` — не вышло, дальше только выход. */
  refresh(): Promise<boolean>
  /** Забыть токены: дальше клиент работает как неавторизованный. */
  forget(): void
}

export type ApiOptions = {
  /** Префикс, на который смонтирован tRPC (`buildServer`, prefix). */
  readonly baseUrl?: string
  readonly fetch?: Fetch
  /**
   * Устройство, от имени которого идут запросы (D29). Уходит
   * заголовком ко всем процедурам, а не полем в тело: это
   * удостоверение владельца драфта, а не часть команды.
   */
  readonly device?: string | undefined
  /**
   * Сессия мастера. Без неё клиент работает как до фазы 3: устройство
   * и ничего больше.
   */
  readonly session?: SessionPort | undefined
}

/**
 * Отказ, который лечится обновлением токена.
 *
 * Только `session_expired` и голая 401. `invalid_credentials`
 * обновлением не лечится — там просто неверный пароль, — а
 * `rate_limited` от повтора становится хуже.
 */
function expired(error: unknown): boolean {
  if (error instanceof AuthRefused) return error.problem.code === 'session_expired'
  return error instanceof ApiError && error.status === 401
}

async function call(
  path: string,
  init: RequestInit & { readonly url: string },
  doFetch: Fetch,
): Promise<unknown> {
  let response: Response
  try {
    response = await doFetch(init.url, init)
  } catch {
    throw new ApiError(`процедура ${path}: сервер недоступен`, null)
  }

  const body: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const failure = failureSchema.safeParse(body)
    const problems = failure.success ? failure.data.error.data?.problems : undefined
    if (problems !== undefined && problems.length > 0)
      throw new CreationRefused(problems)
    const inventory = failure.success ? failure.data.error.data?.inventory : undefined
    if (inventory !== undefined && inventory.length > 0)
      throw new InventoryRefused(inventory)
    const hp = failure.success ? failure.data.error.data?.hp : undefined
    if (hp !== undefined && hp.length > 0) throw new HpRefused(hp)
    const spells = failure.success ? failure.data.error.data?.spells : undefined
    if (spells !== undefined && spells.length > 0) throw new SpellRefused(spells)
    const resources = failure.success ? failure.data.error.data?.resources : undefined
    if (resources !== undefined && resources.length > 0)
      throw new ResourceRefused(resources)
    const content = failure.success ? failure.data.error.data?.content : undefined
    if (content !== undefined && content.length > 0) throw new ContentRefused(content)
    const imported = failure.success ? failure.data.error.data?.import : undefined
    if (imported !== undefined && imported.length > 0) throw new ImportRefused(imported)
    const auth = failure.success ? failure.data.error.data?.auth : undefined
    if (auth !== undefined) throw new AuthRefused(auth)
    const calculation = failure.success
      ? failure.data.error.data?.calculation
      : undefined
    if (calculation !== undefined) throw new CalculationFailed(calculation)
    const validation = failure.success ? failure.data.error.data?.validation : undefined
    if (validation !== undefined && validation.length > 0)
      throw new ValidationRefused(validation)
    throw new ApiError(
      failure.success ? failure.data.error.message : `процедура ${path} отказала`,
      response.status,
    )
  }

  const parsed = successSchema.safeParse(body)
  if (!parsed.success) {
    throw new ApiError(`процедура ${path} ответила неизвестной формой`, response.status)
  }
  return parsed.data.result.data
}

/**
 * Клиент процедур. Схемы выхода — те же, что объявлены на сервере
 * в `.output()`, поэтому расхождение видно сразу и здесь.
 */
export function createApi({
  baseUrl = '/trpc',
  fetch = globalThis.fetch,
  device,
  session,
}: ApiOptions = {}) {
  const identity = device === undefined ? {} : { [DEVICE_TOKEN_HEADER]: device }

  const bearer = (path: string): Record<string, string> => {
    const token = session?.accessTokenFor(path) ?? null
    return token === null ? {} : { authorization: `Bearer ${token}` }
  }

  /**
   * Один запрос с одной повторной попыткой после обновления токена.
   *
   * Повтор ровно один: если и после свежего токена пришёл отказ,
   * дело не в токене, и второе обновление ничего не изменит —
   * зато цикл получится отличный.
   */
  const run = async (
    path: string,
    build: () => RequestInit & { readonly url: string },
  ): Promise<unknown> => {
    try {
      return await call(path, build(), fetch)
    } catch (error) {
      if (session === undefined) throw error
      if (!session.mayRefresh(path) || !expired(error)) throw error
      if (!(await session.refresh())) {
        // Обновиться не вышло — сессии больше нет. Выход, а не
        // ещё одна попытка: это и есть «выход по отказу».
        session.forget()
        throw error
      }
      return await call(path, build(), fetch)
    }
  }

  const query = async (path: string, input: unknown): Promise<unknown> =>
    await run(path, () => ({
      url: `${baseUrl}/${path}?input=${encodeURIComponent(JSON.stringify(input))}`,
      method: 'GET',
      headers: { accept: 'application/json', ...identity, ...bearer(path) },
    }))

  const mutate = async (path: string, input: unknown): Promise<unknown> =>
    await run(path, () => ({
      url: `${baseUrl}/${path}`,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
        ...identity,
        ...bearer(path),
      },
      body: JSON.stringify(input),
    }))

  return {
    /* ── Аутентификация мастера (D8) ─────────────────────────────── */

    async register(input: RegisterInput): Promise<void> {
      registerResultSchema.parse(await mutate('auth.register', input))
    },

    /**
     * Попросить письмо с подтверждением ещё раз (D41).
     *
     * Ответ одинаков во всех случаях — адреса нет, адрес подтверждён,
     * письмо ушло, — поэтому возвращать нечего.
     */
    async resendConfirmation(email: string): Promise<void> {
      resendConfirmationResultSchema.parse(
        await mutate('auth.resendConfirmation', { email }),
      )
    },

    async confirmEmail(token: string): Promise<void> {
      confirmEmailResultSchema.parse(await mutate('auth.confirmEmail', { token }))
    },

    async login(input: LoginInput): Promise<LoginResult> {
      return loginResultSchema.parse(await mutate('auth.login', input))
    },

    /**
     * Обновление токена. Ходит без заголовка авторизации и не пытается
     * обновиться по отказу — оба правила стоят в `SessionPort`,
     * а не здесь: место у них одно на весь клиент.
     */
    async refreshTokens(refreshToken: string): Promise<AuthTokens> {
      const result = refreshResultSchema.parse(
        await mutate('auth.refresh', { refreshToken }),
      )
      return result.tokens
    },

    async logout(refreshToken: string, allDevices: boolean): Promise<number> {
      const result = logoutResultSchema.parse(
        await mutate('auth.logout', { refreshToken, allDevices }),
      )
      return result.closed
    },

    async requestPasswordReset(email: string): Promise<void> {
      requestPasswordResetResultSchema.parse(
        await mutate('auth.requestPasswordReset', { email }),
      )
    },

    async resetPassword(input: ResetPasswordInput): Promise<number> {
      const result = resetPasswordResultSchema.parse(
        await mutate('auth.resetPassword', input),
      )
      return result.closed
    },

    async me(): Promise<MeResult> {
      return meResultSchema.parse(await query('auth.me', undefined))
    },

    /** Перенос драфтов устройства на аккаунт (D33): список явный. */
    async adoptDrafts(characterIds: readonly string[]): Promise<AdoptDraftsResult> {
      return adoptDraftsResultSchema.parse(
        await mutate('auth.adoptDrafts', { characterIds: [...characterIds] }),
      )
    },

    /* ── Кампании и места: сторона мастера (D8) ─────────────────── */

    async listCampaigns(includeArchived = false): Promise<Campaign[]> {
      return campaignListOutputSchema.parse(
        await query('campaigns.list', { includeArchived }),
      )
    },

    async createCampaign(input: CreateCampaignInput): Promise<Campaign> {
      const result = campaignResultSchema.parse(await mutate('campaigns.create', input))
      return result.campaign
    },

    async updateCampaign(input: UpdateCampaignInput): Promise<Campaign> {
      const result = campaignResultSchema.parse(await mutate('campaigns.update', input))
      return result.campaign
    },

    /** Архивация и возврат из архива (D37); удаления у кампании нет. */
    async archiveCampaign(campaignId: string, archived: boolean): Promise<Campaign> {
      const result = campaignResultSchema.parse(
        await mutate('campaigns.archive', { campaignId, archived }),
      )
      return result.campaign
    },

    async listSeats(campaignId: string): Promise<Seat[]> {
      return seatListOutputSchema.parse(await query('campaigns.seats', { campaignId }))
    },

    async createSeat(campaignId: string, label: string): Promise<Seat> {
      const result = seatResultSchema.parse(
        await mutate('campaigns.createSeat', { campaignId, label }),
      )
      return result.seat
    },

    async renameSeat(seatId: string, label: string): Promise<Seat> {
      const result = seatResultSchema.parse(
        await mutate('campaigns.renameSeat', { seatId, label }),
      )
      return result.seat
    },

    /** Персонаж возвращается устройству игрока, а не удаляется (D37). */
    async releaseSeat(seatId: string): Promise<ReleaseSeatResult> {
      return releaseSeatResultSchema.parse(
        await mutate('campaigns.releaseSeat', { seatId }),
      )
    },

    /**
     * Ссылка кампании (D51). Она одна и живёт до отзыва, поэтому
     * запрос идемпотентный: второй вызов вернёт ту же ссылку, а не
     * выпустит новую. Мастер копирует её столько раз, сколько нужно.
     */
    async campaignInvite(campaignId: string): Promise<Campaign> {
      const result = campaignResultSchema.parse(
        await mutate('campaigns.campaignInvite', { campaignId }),
      )
      return result.campaign
    },

    /** Отзыв ссылки кампании: старая перестаёт работать (D51). */
    async revokeCampaignInvite(campaignId: string): Promise<Campaign> {
      const result = campaignResultSchema.parse(
        await mutate('campaigns.revokeCampaignInvite', { campaignId }),
      )
      return result.campaign
    },

    /**
     * Ссылка приходит один раз: в базе лежит хеш, и восстановить её
     * нельзя (D38). Экран обязан дать скопировать её сразу.
     *
     * Персональные одноразовые ссылки из интерфейса убраны (D51);
     * процедура оставлена рабочей ради кампаний, заведённых до неё.
     */
    async issueInvite(seatId: string): Promise<InviteResult> {
      return inviteResultSchema.parse(await mutate('campaigns.issueInvite', { seatId }))
    },

    async revokeInvite(seatId: string): Promise<Seat> {
      const result = seatResultSchema.parse(
        await mutate('campaigns.revokeInvite', { seatId }),
      )
      return result.seat
    },

    async listJoinRequests(campaignId: string): Promise<JoinRequest[]> {
      return joinRequestListOutputSchema.parse(
        await query('campaigns.requests', { campaignId }),
      )
    },

    /** При отказе места нет; при подтверждении заявки по ссылке оно создаётся (D51). */
    async resolveJoinRequest(
      requestId: string,
      approved: boolean,
      /** Что сказать игроку при отказе (D61). */
      note?: string,
    ): Promise<Seat | null> {
      const result = resolveJoinRequestResultSchema.parse(
        await mutate('campaigns.resolveRequest', {
          requestId,
          approved,
          ...(note === undefined ? {} : { note }),
        }),
      )
      return result.seat
    },

    /* ── Сторона игрока ─────────────────────────────────────────── */

    /**
     * Что за кампания по ту сторону ссылки (D51). Показывается до
     * заявки, иначе игрок не понимает, куда пришёл. Название и сколько
     * мест занято — всё; состав партии остаётся закрытым до
     * подтверждения (D38).
     */
    async invitePreview(token: string): Promise<InvitePreview> {
      return invitePreviewSchema.parse(
        await query('campaigns.invitePreview', { token }),
      )
    },

    /**
     * Заявка по ссылке кампании (D51). Место не занимается: оно
     * появится, когда мастер подтвердит. Если мест не осталось,
     * ответ придёт со `status: 'rejected'` и причиной — это не ошибка,
     * а нормальный исход.
     */
    /**
     * Заявка по ссылке (D51, D55): свой персонаж по идентификатору
     * либо свой же с другого устройства — по коду.
     */
    async requestJoin(
      token: string,
      character: JoinCharacterRef,
      origin: JoinCharacterOrigin,
    ): Promise<RequestJoinResult> {
      return requestJoinResultSchema.parse(
        await mutate('campaigns.requestJoin', { token, character, origin }),
      )
    },

    /**
     * Переход по ссылке-приглашению (D38). Ответ не несёт ничего
     * о кампании: до решения мастера игрок знает только то, что
     * ссылка была настоящей.
     */
    async claimInvite(token: string): Promise<ClaimInviteResult> {
      return claimInviteResultSchema.parse(
        await mutate('campaigns.claimInvite', { token }),
      )
    },

    /** Возврат за своё место с нового устройства по коду (D38). */
    async requestDeviceChange(code: string): Promise<ClaimInviteResult> {
      return claimInviteResultSchema.parse(
        await mutate('campaigns.requestDeviceChange', { code }),
      )
    },

    async mySeats(): Promise<SeatState[]> {
      return mySeatsOutputSchema.parse(await query('campaigns.mySeats', undefined))
    },

    /** Подбор своего драфта за место по коду (D29). */
    async attachCharacter(seatId: string, code: string): Promise<Seat> {
      const result = seatResultSchema.parse(
        await mutate('campaigns.attachCharacter', { seatId, code }),
      )
      return result.seat
    },

    async creationPlan(input: CreationPlanInput): Promise<CreationPlan> {
      return creationPlanSchema.parse(await query('creation.plan', input))
    },

    /* ── Пользовательский контент (D53) ──────────────────────────── */

    async listContent(type: CustomType | null): Promise<CustomRecord[]> {
      return customListOutputSchema.parse(await query('content.list', { type }))
    },

    /**
     * Поля формы для вида записи: приходят с сервера, где считаются
     * из схемы. Считать их здесь значило бы притащить в браузер схемы
     * всех заклинаний, предметов и классов.
     */
    async contentForm(type: CustomType): Promise<readonly FormField[]> {
      const result = contentFormOutputSchema.parse(
        await query('content.form', { type }),
      )
      return result.fields
    },

    async contentVocabulary(seatId: string | null): Promise<EffectVocabulary> {
      return effectVocabularySchema.parse(await query('content.vocabulary', { seatId }))
    },

    async contentPicks(
      type: CustomType,
      seatId: string | null,
    ): Promise<ContentPick[]> {
      return contentPickOutputSchema.parse(
        await query('content.picks', { type, seatId }),
      )
    },

    async contentCopy(id: string): Promise<ContentCopy> {
      return contentCopyOutputSchema.parse(await query('content.copy', { id }))
    },

    async saveContent(input: SaveCustomInput): Promise<CustomRecord> {
      const result = customRecordResultSchema.parse(await mutate('content.save', input))
      return result.record
    },

    async removeContent(id: string, confirmedUsedBy: number | null): Promise<boolean> {
      const result = removeCustomResultSchema.parse(
        await mutate('content.remove', { id, confirmedUsedBy }),
      )
      return result.removed
    },

    /** Кампания плюс драфты этого устройства; `null` — только драфты. */
    async listCharacters(campaignId: string | null): Promise<CharacterSummary[]> {
      return characterListOutputSchema.parse(
        await query('characters.list', { campaignId }),
      )
    },

    /* ── Файл персонажа (D54) ─────────────────────────────────────── */

    async exportCharacter(characterId: string): Promise<ExportCharacterResult> {
      return exportCharacterResultSchema.parse(
        await query('characters.export', { characterId }),
      )
    },

    /**
     * Загрузка файла. Файл уходит на сервер как есть: разбирает его
     * сервер — это единственные данные в проекте, пришедшие снаружи.
     */
    async importCharacter(
      file: string,
      adoptContent: boolean,
    ): Promise<ImportCharacterResult> {
      return importCharacterResultSchema.parse(
        await mutate('characters.import', { file, adoptContent }),
      )
    },

    async characterSheet(characterId: string): Promise<CharacterSheet> {
      return characterSheetSchema.parse(
        await query('characters.sheet', { characterId }),
      )
    },

    async createCharacter(input: CreateCharacterInput): Promise<CharacterSummary> {
      const result = createCharacterResultSchema.parse(
        await mutate('characters.create', input),
      )
      return result.character
    },

    /**
     * Действие с инвентарём — команда, а не правка (D3): уходит
     * «надень кольчугу», приходит пересчитанный лист целиком.
     * Клиент не меняет ни список, ни КД у себя: он показывает то,
     * что вернул сервер, иначе после отказа на экране осталось бы
     * состояние, которого нет ни в одном журнале.
     */
    async inventoryAction(
      characterId: string,
      action: InventoryAction,
    ): Promise<CharacterSheet> {
      const result = inventoryCommandResultSchema.parse(
        await mutate('characters.inventory', { characterId, action }),
      )
      return result.sheet
    },

    /** Урон, лечение, временные хиты — командой (D3). */
    async hpAction(characterId: string, action: HpAction): Promise<CharacterSheet> {
      const result = hpCommandResultSchema.parse(
        await mutate('characters.hp', { characterId, action }),
      )
      return result.sheet
    },

    /** Подготовка, ячейки и сосредоточение — командой (D3). */
    async spellAction(
      characterId: string,
      action: SpellAction,
    ): Promise<CharacterSheet> {
      const result = spellCommandResultSchema.parse(
        await mutate('characters.spell', { characterId, action }),
      )
      return result.sheet
    },

    /** Счётчик ресурса: трата, возврат, свой счётчик (D3, D56). */
    async resourceAction(
      characterId: string,
      action: ResourceAction,
    ): Promise<CharacterSheet> {
      const result = hpCommandResultSchema.parse(
        await mutate('characters.resource', { characterId, action }),
      )
      return result.sheet
    },

    /** Правка имени и описания персонажа (D4). */
    async updateCharacter(input: UpdateCharacterInput): Promise<CharacterSheet> {
      const result = hpCommandResultSchema.parse(
        await mutate('characters.update', input),
      )
      return result.sheet
    },

    /**
     * Отдых. Долгий возвращает хиты, кости и ячейки; короткий —
     * только ячейки магии договора: хиты в нём возвращают костями.
     */
    async rest(characterId: string, kind: 'long' | 'short'): Promise<CharacterSheet> {
      const result = hpCommandResultSchema.parse(
        await mutate('characters.rest', { characterId, kind }),
      )
      return result.sheet
    },

    /** План повышения уровня (D31). */
    async levelPlan(characterId: string): Promise<LevelPlan> {
      return levelPlanSchema.parse(await query('characters.levelPlan', { characterId }))
    },

    async levelUp(input: LevelUpCommandInput): Promise<CharacterSheet> {
      const result = levelUpCommandResultSchema.parse(
        await mutate('characters.levelUp', input),
      )
      return result.sheet
    },

    /**
     * Визард на правку (D62): сделанные выборы и судьба последней
     * заявки на правку.
     */
    async creationEdit(characterId: string): Promise<CreationEdit> {
      return creationEditSchema.parse(
        await query('characters.creationEdit', { characterId }),
      )
    },

    /**
     * Правка выборов (D62): драфт правится сразу, персонаж за столом —
     * заявкой мастеру. Отказ проверки приходит тем же `CreationRefused`,
     * что и при создании: проверка у них одна.
     */
    async editCreation(
      characterId: string,
      choices: CreationChoices,
    ): Promise<EditCreationResult> {
      return editCreationResultSchema.parse(
        await mutate('characters.editCreation', { characterId, choices }),
      )
    },

    /** Откат последнего повышения, пока за ним ничего не записано. */
    async levelDown(characterId: string): Promise<CharacterSheet> {
      const result = levelUpCommandResultSchema.parse(
        await mutate('characters.levelDown', { characterId }),
      )
      return result.sheet
    },

    /** Список заклинаний класса: из чего готовят. */
    async classSpells(characterId: string): Promise<SheetSpell[]> {
      return classSpellsOutputSchema.parse(
        await query('characters.classSpells', { characterId }),
      )
    },

    /**
     * Бросок (D12). Кости бросает сервер: результату, посчитанному
     * в браузере игрока, за столом верить не с чего (D3).
     */
    async roll(input: RollCommandInput): Promise<RollCommandResult> {
      return rollCommandResultSchema.parse(await mutate('dice.roll', input))
    },

    async rollLog(characterId: string, limit = 20): Promise<RollEntry[]> {
      return rollLogOutputSchema.parse(await query('dice.log', { characterId, limit }))
    },

    /* ── Панель мастера (D3, D10) ────────────────────────────────── */

    /** Что мастер может выдать и повесить: списки, а не каталог. */
    async masterCatalogue(campaignId: string): Promise<MasterCatalogue> {
      return masterCatalogueSchema.parse(
        await query('campaigns.catalogue', { campaignId }),
      )
    },

    /** Сводка партии: все персонажи стола сразу. */
    async party(campaignId: string): Promise<PartyMember[]> {
      return partyOutputSchema.parse(await query('campaigns.party', { campaignId }))
    },

    /**
     * Команда мастера над персонажем.
     *
     * Ответом приходит пересчитанная строка сводки: клиенту нечего
     * складывать самому и негде разойтись с сервером (D3).
     */
    async command(campaignId: string, command: MasterCommand): Promise<PartyMember> {
      const result = masterCommandResultSchema.parse(
        await mutate('campaigns.command', { campaignId, command }),
      )
      return result.member
    },

    /** Отдых объявляет мастер на всю партию. */
    async declareRest(
      campaignId: string,
      kind: 'short' | 'long',
    ): Promise<DeclareRestResult> {
      return declareRestResultSchema.parse(
        await mutate('campaigns.rest', { campaignId, kind }),
      )
    },

    /** Начисление опыта себе: только у драфта (D31). */
    async awardXp(characterId: string, amount: number): Promise<CharacterSheet> {
      const result = hpCommandResultSchema.parse(
        await mutate('characters.awardXp', { characterId, amount }),
      )
      return result.sheet
    },

    /* ── Экран боя по инициативе ─────────────────────────────────── */

    async initiative(campaignId: string): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await query('campaigns.initiative', { campaignId }),
      )
      return result.initiative
    },

    async startCombat(campaignId: string, rollAll: boolean): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.startCombat', { campaignId, rollAll }),
      )
      return result.initiative
    },

    async endCombat(campaignId: string): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.endCombat', { campaignId }),
      )
      return result.initiative
    },

    /** Строка без персонажа: волк, три гоблина, ловушка. */
    async addInitiativeRow(
      campaignId: string,
      label: string,
      value: number | null,
    ): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.addInitiativeRow', { campaignId, label, value }),
      )
      return result.initiative
    },

    async removeInitiativeRow(
      campaignId: string,
      rowId: string,
    ): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.removeInitiativeRow', { campaignId, rowId }),
      )
      return result.initiative
    },

    /* ── Монстры в бою (D50) ─────────────────────────────────────── */

    /** Справочник монстров: мастер выбирает запись, а не печатает имя. */
    async monsters(campaignId: string): Promise<MonsterListEntry[]> {
      const result = monsterListOutputSchema.parse(
        await query('campaigns.monsters', { campaignId }),
      )
      return result.monsters
    },

    async addMonster(
      campaignId: string,
      monsterId: string,
      count: number,
    ): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.addMonster', { campaignId, monsterId, count }),
      )
      return result.initiative
    },

    async monsterHp(
      campaignId: string,
      rowId: string,
      kind: 'damage' | 'heal',
      amount: number,
    ): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.monsterHp', {
          campaignId,
          rowId,
          action: { kind, amount },
        }),
      )
      return result.initiative
    },

    async monsterCondition(
      campaignId: string,
      rowId: string,
      condition: string,
      on: boolean,
    ): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.monsterCondition', {
          campaignId,
          rowId,
          condition,
          on,
        }),
      )
      return result.initiative
    },

    /** Атака монстра: бросок и урон уходят в общий журнал (D12). */
    async rollMonsterAttack(
      campaignId: string,
      rowId: string,
      attackKey: string,
      hidden: boolean,
    ): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.rollMonsterAttack', {
          campaignId,
          rowId,
          attackKey,
          hidden,
        }),
      )
      return result.initiative
    },

    /** Бросок инициативы: `null` — своя строка (игрок бросает себе). */
    async rollInitiative(
      campaignId: string,
      rowId: string | null,
    ): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.rollInitiative', { campaignId, rowId }),
      )
      return result.initiative
    },

    async nextTurn(campaignId: string): Promise<InitiativeState> {
      const result = initiativeResultSchema.parse(
        await mutate('campaigns.nextTurn', { campaignId }),
      )
      return result.initiative
    },

    /** Общий журнал бросков: скрытый приходит без результата (D12). */
    async campaignRolls(campaignId: string, limit = 50): Promise<CampaignRoll[]> {
      return campaignRollsOutputSchema.parse(
        await query('campaigns.rolls', { campaignId, limit }),
      )
    },

    /**
     * Поток событий кампании (D2).
     *
     * Живёт здесь, рядом с процедурами, ради одного: удостоверение
     * собирается тем же способом, что и у них. Токен мастера
     * и устройство игрока уходят заголовками, а протухший токен
     * обновляется той же сессией, что и на обычном запросе.
     *
     * Путь у потока свой (`/events`), не префикс tRPC: у процедуры
     * один ответ, а здесь соединение, живущее часами.
     */
    watchCampaign(
      campaignId: string,
      after: number,
      handlers: StreamHandlers,
    ): CampaignStream {
      const path = 'campaigns.events'
      return openCampaignStream(
        {
          campaignId,
          after,
          fetch,
          headers: () => ({ ...identity, ...bearer(path) }),
          onUnauthorized: async () =>
            session !== undefined && session.mayRefresh(path)
              ? await session.refresh()
              : false,
        },
        handlers,
      )
    },
  }
}

export type Api = ReturnType<typeof createApi>
